import { nanoid } from 'nanoid/non-secure';

import { LOCAL_VECTOR_ATTRIBUTE } from '@adflow/types';
import type {
  AdElement,
  AdElementText,
  AdElementVector,
  ElementRect,
  ElementTool,
  EditorState,
  NewElementSpec,
} from '@adflow/types';

import { getVariantLabel } from '../components/Rightsidebar/TextElementControls/utils/fontUtils';

export const TEXT_DEFAULT_SIZE = { width: 120, height: 24 } as const;
export const VECTOR_DEFAULT_SIZE = { width: 80, height: 80 } as const;
/** Smallest side a dragged vector can have. */
export const VECTOR_MIN_SIZE = 8;
export const TEXT_MIN_SIZE = 1;
export const TEXT_DEFAULT_COLOR = '#FFFFFF';
export const TEXT_DEFAULT_FONT_SIZE = 16;
export const TEXT_DEFAULT_LINE_HEIGHT = 12;
export const DEFAULT_VECTOR_FILL = '#3367D6';

const MARK = `${LOCAL_VECTOR_ATTRIBUTE}="true"`;

const FALLBACK_FONT_FAMILY = 'Source Sans 3';

const BASE_NAMES: Record<ElementTool, string> = {
  TEXT: 'Text',
  RECTANGLE: 'Rectangle',
  ELLIPSE: 'Ellipse',
  TRIANGLE: 'Triangle',
};

export function isVectorTool(tool: ElementTool): boolean {
  return tool !== 'TEXT';
}

export function defaultSizeForTool(tool: ElementTool): { width: number; height: number } {
  return tool === 'TEXT' ? { ...TEXT_DEFAULT_SIZE } : { ...VECTOR_DEFAULT_SIZE };
}

/** The topmost layer sits one above everything else, so it wins hit tests and paints last. */
export function nextTopZ(elements: readonly AdElement[]): number {
  return elements.reduce((max, element) => Math.max(max, element.position.z ?? 0), 0) + 1;
}

/** `Text 1`, `Text 2`, ... The first number that no element uses yet. */
export function uniqueElementName(tool: ElementTool, elements: readonly AdElement[]): string {
  const taken = new Set<string>();
  elements.forEach((element) => {
    taken.add(element.name);
    taken.add(element.id);
  });
  let counter = 1;
  while (taken.has(`${BASE_NAMES[tool]} ${counter}`)) {
    counter += 1;
  }
  return `${BASE_NAMES[tool]} ${counter}`;
}

function pickFont(customFonts: EditorState['customFonts']) {
  const regularSourceSans = customFonts.find(
    (font) =>
      /source\s*sans/i.test(font.fontFamily) &&
      (font.weight ?? 400) === 400 &&
      (font.style || 'normal') === 'normal',
  );
  if (regularSourceSans) {
    return {
      fontFamily: regularSourceSans.fontFamily,
      fontVariant: getVariantLabel(400, 'normal', regularSourceSans.variant),
      fontWeight: 400,
      fontStyle: 'normal',
      fontStretch: regularSourceSans.stretch || 'normal',
    };
  }
  // Not loaded for this advertiser: the existing font validation flags it before saving.
  return {
    fontFamily: FALLBACK_FONT_FAMILY,
    fontVariant: null,
    fontWeight: 400,
    fontStyle: 'normal',
    fontStretch: 'normal',
  };
}

type Context = {
  id?: string;
  z: number;
  name: string;
  customFonts?: EditorState['customFonts'];
};

export function createTextElement(
  rect: ElementRect,
  { id = nanoid(), z, name, customFonts = [] }: Context,
  content = '',
): AdElementText {
  return {
    id,
    name,
    type: 'TEXT',
    position: { x: rect.x, y: rect.y, z },
    size: { width: rect.width, height: rect.height },
    sources: [],
    data: {
      content,
      dynamicFont: { enabled: false },
      style: {
        color: TEXT_DEFAULT_COLOR,
        fontSize: TEXT_DEFAULT_FONT_SIZE,
        lineHeight: TEXT_DEFAULT_LINE_HEIGHT,
        textAlign: 'left',
        textAlignVertical: 'top',
        ...pickFont(customFonts),
      },
    },
  };
}

/**
 * A fill-less shape in its own box (viewBox units). The fill comes from the `<g>` that wraps it
 * at render, and the marker attribute tells the renderer not to shift it by the element position.
 */
export function vectorShapeContent(
  tool: Exclude<ElementTool, 'TEXT'>,
  width: number,
  height: number,
): string {
  switch (tool) {
    case 'ELLIPSE':
      return `<ellipse ${MARK} cx="${width / 2}" cy="${height / 2}" rx="${width / 2}" ry="${
        height / 2
      }"/>`;
    case 'TRIANGLE':
      return `<polygon ${MARK} points="${width / 2},0 ${width},${height} 0,${height}"/>`;
    case 'RECTANGLE':
    default:
      return `<rect ${MARK} x="0" y="0" width="${width}" height="${height}"/>`;
  }
}

export function createVectorElement(
  tool: Exclude<ElementTool, 'TEXT'>,
  rect: ElementRect,
  { id = nanoid(), z, name }: Context,
): AdElementVector {
  return {
    id,
    name,
    type: 'VECTOR',
    position: { x: rect.x, y: rect.y, z },
    size: { width: rect.width, height: rect.height },
    sources: [],
    data: {
      content: vectorShapeContent(tool, rect.width, rect.height),
      viewBox: { width: rect.width, height: rect.height },
      color: {
        fill: { value: DEFAULT_VECTOR_FILL, type: null, source: null },
        stroke: null,
      },
    },
  };
}

/** Builds the element a spec describes and puts it on top of `elements`. */
export function createElementFromSpec(
  spec: NewElementSpec,
  elements: readonly AdElement[],
  customFonts: EditorState['customFonts'],
): AdElement {
  const rect = normalizeSpecRect(spec);
  const context: Context = {
    z: nextTopZ(elements),
    name: spec.name?.trim() || uniqueElementName(spec.tool, elements),
    customFonts,
  };
  if (spec.tool === 'TEXT') {
    return createTextElement(rect, context, spec.content ?? '');
  }
  return createVectorElement(spec.tool, rect, context);
}

function normalizeSpecRect(spec: NewElementSpec): ElementRect {
  const min = isVectorTool(spec.tool) ? VECTOR_MIN_SIZE : TEXT_MIN_SIZE;
  const fallback = defaultSizeForTool(spec.tool);
  return {
    x: Math.round(spec.rect.x),
    y: Math.round(spec.rect.y),
    width: Math.max(min, Math.round(spec.rect.width ?? fallback.width)),
    height: Math.max(min, Math.round(spec.rect.height ?? fallback.height)),
  };
}

/**
 * The rectangle a draw gesture produces.
 * A click (no drag) gives the tool's default size, centred on the press point. A drag spans the two
 * corners, whichever way it goes. Vectors keep a minimum side, and Shift squares a vector off
 * along its longer side.
 */
export function rectFromGesture(
  tool: ElementTool,
  origin: { x: number; y: number },
  current: { x: number; y: number },
  options: { dragged: boolean; square?: boolean },
): ElementRect {
  if (!options.dragged) {
    const size = defaultSizeForTool(tool);
    // Text puts its top-left corner on the click. Vectors are centred on it.
    if (tool === 'TEXT') {
      return { x: Math.round(origin.x), y: Math.round(origin.y), ...size };
    }
    return {
      x: Math.round(origin.x - size.width / 2),
      y: Math.round(origin.y - size.height / 2),
      ...size,
    };
  }

  let dx = current.x - origin.x;
  let dy = current.y - origin.y;
  const min = isVectorTool(tool) ? VECTOR_MIN_SIZE : TEXT_MIN_SIZE;

  if (options.square && isVectorTool(tool)) {
    const side = Math.max(Math.abs(dx), Math.abs(dy));
    dx = Math.sign(dx || 1) * side;
    dy = Math.sign(dy || 1) * side;
  }

  let width = Math.max(min, Math.abs(dx));
  let height = Math.max(min, Math.abs(dy));
  if (options.square && isVectorTool(tool)) {
    width = Math.max(width, height);
    height = width;
  }

  // Grow away from the press point so the corner under the cursor never jumps.
  const x = dx < 0 ? origin.x - width : origin.x;
  const y = dy < 0 ? origin.y - height : origin.y;
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(width),
    height: Math.round(height),
  };
}
